import { BottomSheetModalProvider } from '@gorhom/bottom-sheet';
import type { FC, PropsWithChildren } from 'react';
import { useCallback, useMemo, useState } from 'react';
import type { ViewStyle } from 'react-native';
import { Portal } from 'react-native-paper';
import Reanimated, { useAnimatedStyle } from 'react-native-reanimated';

import { Alert, type AlertProps } from '@/features/popup/components/Alert';
import { Toast } from '@/features/popup/components/Toast';
import type { ToastProps } from '@/features/popup/components/Toast';
import { BottomModalProvider } from '@/features/popup/provider/BottomModalProvider';
import { PopupContext } from '@/features/popup/provider/usePopup';
import type { PopupContextValues } from '@/features/popup/provider/usePopup';
import { useExtendedKeyboardAnimation } from '@/shared/hooks/useExtendedKeyboardAnimation';

export const PopupProvider: FC<PropsWithChildren> = ({ children }) => {
  const [alerts, setAlerts] = useState<AlertProps[] | undefined>();

  const handleCloseAlert = useCallback((title: string) => {
    setAlerts((prevValues) =>
      prevValues?.filter((a) => a.labels.title !== title)
    );
  }, []);

  const showAlert = useCallback(
    (alert: Omit<AlertProps, 'onClose' | 'isVisible'>) =>
      setAlerts((prevValues) => [
        ...(prevValues ?? []),
        {
          ...alert,
          isVisible: true,
          onClose: () => {
            handleCloseAlert(alert.labels.title);
          }
        }
      ]),
    [handleCloseAlert]
  );

  const clearAllAlerts = useCallback(() => setAlerts(undefined), []);

  const [toast, setToast] = useState<ToastProps | undefined>();

  const showToast = useCallback(
    (toast: Omit<ToastProps, 'onClose' | 'isVisible'>) =>
      setToast({
        ...toast,
        isVisible: true,
        onClose: () => {
          setToast(undefined);
        }
      }),
    []
  );

  const popupContextValues: PopupContextValues = useMemo(
    () => ({
      clearAllAlerts,
      showAlert,
      showToast
    }),
    [clearAllAlerts, showAlert, showToast]
  );
  const { height } = useExtendedKeyboardAnimation();
  const animatedStyle = useAnimatedStyle<ViewStyle>(() => ({
    transform: [{ translateY: -height.value }]
  }));

  return (
    <PopupContext.Provider value={popupContextValues}>
      <Portal>
        {alerts?.map((props, index) => (
          <Alert key={index} {...props} />
        ))}
      </Portal>
      <BottomSheetModalProvider>
        <BottomModalProvider>{children}</BottomModalProvider>
      </BottomSheetModalProvider>
      <Reanimated.View style={animatedStyle}>
        {toast && <Toast {...toast} />}
      </Reanimated.View>
    </PopupContext.Provider>
  );
};
